// src/padplanner/RuleSets.jsx
//
// The library page's Clearance rules (S5b): the org's one lineage of rule
// sets (D-S5-2), with the template sets' lifecycle. A draft is created
// (copied from the published set's rules, or named when there is none),
// edited, published or discarded; a published set gets a new version or is
// retired. Plans are checked against the published version only.
//
// WHO SEES WHAT (D-S5-4). The server says: can_draft (can_edit_site_plans,
// not a support session) creates, edits and discards drafts, and only those
// callers are sent drafts at all; can_publish (org_admin as well) publishes
// and retires. A control the caller cannot use is not rendered. The server
// refuses anyway, and a refusal is shown verbatim with its code.
//
// THE RULE FORM checks the fields as the server does (ruleVocabulary.js, a
// tested copy of lib/padplanner/rules.js) before anything is sent. A
// selector's template keys are offered from the org's own sets and the
// published standard set.
import { useEffect, useMemo, useState } from "react";
import { SectionLabel } from "./SidePanel";
import {
  listTemplateSets, getTemplateSet, getRuleSet, createRuleSet, updateRuleSet, discardRuleSet, addRule, updateRule,
  removeRule, publishRuleSet, retireRuleSet,
} from "../api/padplannerApi";
import { CATEGORY_LABELS } from "./templateVocabulary";
import { LINE_TYPES, LINE_TYPE_LABELS } from "./lineVocabulary";
import {
  SELECTOR_CATEGORIES, RULE_RELATIONS, RULE_SEVERITIES, DISTANCE_RELATIONS, RELATION_LABELS, SEVERITY_LABELS,
  validateRuleFields, selectorText, relationText, serverRefusal, fromSelector, toSelector,
} from "./ruleVocabulary";

export const RULES_STATUS_WORDS = Object.freeze({ draft: "Draft", published: "Published", retired: "Retired" });
export const DRAFT_NOT_USED_TEXT = "Plans are checked against the published version only. This draft is not used until it is published.";
export const NO_PUBLISHED_RULES_TEXT = "No clearance rules are published: plans are not checked.";

const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";

/** The left panel's list of rule sets, newest version first. */
export function RuleSetList({ list, selectedId, onSelect, onCreate }) {
  const sets = list?.sets ?? [];
  const published = sets.find((s) => s.status === "published") ?? null;
  const draft = sets.find((s) => s.status === "draft") ?? null;
  const [name, setName] = useState("");
  return (
    <section className="flex flex-col gap-1.5" aria-label="Clearance rules" data-testid="pp-rule-sets">
      <SectionLabel>Clearance rules</SectionLabel>
      <p className="op-meta" data-testid="pp-rules-published">
        {published ? `Published: ${published.name} v${published.version}. Plans are checked against it.` : NO_PUBLISHED_RULES_TEXT}
      </p>
      {sets.map((s) => (
        <button key={s.id} type="button" aria-pressed={selectedId === s.id} data-testid="pp-rule-set" onClick={() => onSelect(s.id)}
          className={`flex items-center justify-between gap-2 rounded border px-2.5 py-2 text-left text-[13px] ${selectedId === s.id ? "border-accent-action bg-accent-action/10" : "border-bg-border hover:border-fg-secondary"}`}>
          <span className="min-w-0 truncate text-fg-primary">{s.name} v{s.version}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="op-chip px-1.5 py-0" data-status={s.status}>{RULES_STATUS_WORDS[s.status] ?? s.status}</span>
            <span className="op-mono text-[11px] text-fg-secondary">{s.rule_count}</span>
          </span>
        </button>
      ))}
      {list?.can_draft && !draft && !published && (
        <form className="flex gap-2" aria-label="Start clearance rules" onSubmit={async (e) => {
          e.preventDefault();
          if (await onCreate({ name: name.trim() })) setName("");
        }}>
          <input className={`${field} min-w-0 flex-1`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
            placeholder="Rule set name" aria-label="Rule set name" />
          <button type="submit" className="op-btn op-btn--primary op-btn--sm shrink-0" disabled={!name.trim()}>Create draft</button>
        </form>
      )}
    </section>
  );
}

/** The template keys a selector can name: the org's own sets and the published standard set. */
function useTemplateKeys(enabled) {
  const [keys, setKeys] = useState(null);
  useEffect(() => {
    if (!enabled || keys) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await listTemplateSets();
        const ids = (list.sets ?? []).filter((s) => !s.standard || s.status === "published").map((s) => s.id);
        const details = await Promise.all(ids.map((id) => getTemplateSet(id).catch(() => null)));
        const byKey = new Map();
        for (const d of details) for (const t of d?.templates ?? []) if (!byKey.has(t.key)) byKey.set(t.key, t.name);
        if (!cancelled) setKeys([...byKey].map(([key, name]) => ({ key, name })).sort((a, b) => a.key.localeCompare(b.key)));
      } catch {
        if (!cancelled) setKeys([]);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, keys]);
  return keys;
}

/** The detail of one rule set: its lifecycle controls and its rules. */
export function RuleSetDetail({ setId, list, act, onDiscarded }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    // The page keys this component by setId: a new set is a new mount.
    let cancelled = false;
    getRuleSet(setId).then((r) => { if (!cancelled) { setDetail(r); setName(r.set.name); } })
      .catch((e) => { if (!cancelled) setError(serverRefusal(e)); });
    return () => { cancelled = true; };
  }, [setId]);

  const canDraft = list?.can_draft === true;
  const canPublish = list?.can_publish === true;
  const draftExists = (list?.sets ?? []).some((s) => s.status === "draft");
  const published = (list?.sets ?? []).find((s) => s.status === "published") ?? null;
  const set = detail?.set;
  const draft = set?.status === "draft";
  const keys = useTemplateKeys(Boolean(draft && canDraft));

  /** A write: adopt the set it answers, or show the refusal. */
  async function run(fn, { reload = true } = {}) {
    setError(null);
    const r = await act(fn);
    if (r.error) { setError(serverRefusal(r.error)); return null; }
    if (r.value?.set) { setDetail(r.value); setName(r.value.set.name); }
    else if (reload && r.value?.discarded !== true) { const d = await getRuleSet(setId).catch(() => null); if (d) setDetail(d); }
    return r.value ?? true;
  }

  if (error && !detail) return <p className="text-sm text-accent-red" role="alert">{error}</p>;
  if (!detail) return <p className="op-meta">Loading…</p>;

  async function publish() {
    if (!window.confirm(`Publish ${set.name} v${set.version}? Plans are checked against it from now on${published ? `, and v${published.version} is retired` : ""}.`)) return;
    await run(() => publishRuleSet(set.id));
  }
  async function retire() {
    if (!window.confirm(`Retire ${set.name} v${set.version}? Plans are then checked against no rules until another version is published.`)) return;
    await run(() => retireRuleSet(set.id));
  }
  async function discard() {
    if (!window.confirm(`Discard ${set.name} v${set.version} and its rules? This can't be undone.`)) return;
    const r = await run(() => discardRuleSet(set.id), { reload: false });
    if (r?.discarded) onDiscarded();
  }

  return (
    <div className="flex max-w-[960px] flex-col gap-4" data-testid="pp-rule-set-detail">
      <div className="flex flex-col gap-1.5">
        {renaming ? (
          <form className="flex gap-2" aria-label="Rename rule set" onSubmit={async (e) => {
            e.preventDefault();
            if (await run(() => updateRuleSet(set.id, { name: name.trim() }))) setRenaming(false);
          }}>
            <input className={`${field} w-72`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label="Rule set name" />
            <button type="submit" className="op-btn op-btn--primary op-btn--sm">Save</button>
            <button type="button" className="op-btn op-btn--sm" onClick={() => { setName(set.name); setRenaming(false); }}>Cancel</button>
          </form>
        ) : (
          <h2 className="font-display text-[17px] font-semibold text-fg-primary">Clearance rules · {set.name} v{set.version}</h2>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="op-chip px-1.5 py-0" data-testid="pp-rule-set-status">{RULES_STATUS_WORDS[set.status] ?? set.status}</span>
          <span className="op-meta">
            {set.published_at ? `Published${set.published_by_display_name ? ` by ${set.published_by_display_name}` : ""}` : `Started by ${set.created_by_display_name ?? "—"}`}
            {set.retired_at ? ` · retired${set.retired_by_display_name ? ` by ${set.retired_by_display_name}` : ""}` : ""}
          </span>
        </div>
        <p className="op-meta" data-testid="pp-rule-set-note">
          {draft ? `${DRAFT_NOT_USED_TEXT}${published ? ` Published now: v${published.version}.` : ""}`
            : set.status === "published" ? "This is the published version: every plan's checks run against it. To change it, start a new version."
            : "Retired: no plan is checked against it."}
        </p>
        <div className="flex flex-wrap gap-2 pt-1" role="group" aria-label="Rule set actions">
          {draft && canDraft && !renaming && <button type="button" className="op-btn op-btn--sm" onClick={() => setRenaming(true)}>Rename</button>}
          {draft && canPublish && <button type="button" className="op-btn op-btn--primary op-btn--sm" onClick={publish}>Publish</button>}
          {draft && canDraft && <button type="button" className="op-btn op-btn--sm" onClick={discard}>Discard draft</button>}
          {set.status === "published" && canDraft && !draftExists && (
            <button type="button" className="op-btn op-btn--sm" onClick={() => run(() => createRuleSet({}), { reload: false })}>New version</button>
          )}
          {set.status === "published" && canPublish && <button type="button" className="op-btn op-btn--sm" onClick={retire}>Retire</button>}
        </div>
        {error && <p className="text-sm text-accent-red" role="alert" data-testid="pp-rules-error">{error}</p>}
      </div>

      <table className="w-full text-left text-[12.5px]">
        <thead>
          <tr className="op-label uppercase">
            <th className="py-1.5 pr-2 font-normal">Key</th>
            <th className="py-1.5 pr-2 font-normal">Rule</th>
            <th className="py-1.5 pr-2 font-normal">Severity</th>
            <th className="py-1.5 pr-2 font-normal">Message</th>
            {draft && canDraft && <th className="py-1.5 font-normal" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {detail.rules.map((r) => (editingId === r.id ? (
            <tr key={r.id}><td colSpan={5} className="py-2">
              <RuleForm rule={r} keys={keys} onCancel={() => setEditingId(null)} onSubmit={async (body) => {
                if (await run(() => updateRule(set.id, r.id, body))) setEditingId(null);
              }} />
            </td></tr>
          ) : (
            <tr key={r.id} className="border-t border-bg-border align-top" data-testid="pp-rule">
              <td className="py-1.5 pr-2 op-mono text-fg-secondary">{r.rule_key}</td>
              <td className="py-1.5 pr-2 text-fg-primary">
                {selectorText(r.subject_selector, CATEGORY_LABELS)} · {relationText(r)}
                {r.object_selector ? ` · ${selectorText(r.object_selector, CATEGORY_LABELS)}` : ""}
              </td>
              <td className="py-1.5 pr-2">{SEVERITY_LABELS[r.severity] ?? r.severity}</td>
              <td className="py-1.5 pr-2">{r.message}</td>
              {draft && canDraft && (
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="op-meta mr-2 hover:text-fg-primary" aria-label={`Edit ${r.rule_key}`} onClick={() => setEditingId(r.id)}>Edit</button>
                  <button type="button" className="op-meta hover:text-fg-primary" aria-label={`Remove ${r.rule_key}`} onClick={async () => {
                    if (window.confirm(`Remove ${r.rule_key} from this draft?`)) await run(() => removeRule(set.id, r.id));
                  }}>Remove</button>
                </td>
              )}
            </tr>
          )))}
        </tbody>
      </table>
      {detail.rules.length === 0 && <p className="op-meta">No rules yet.{draft ? " A rule set needs at least one to be published." : ""}</p>}
      {draft && canDraft && (
        <section className="flex flex-col gap-2 border-t border-bg-border pt-3" aria-label="Add a rule">
          <SectionLabel>Add a rule</SectionLabel>
          <RuleForm keys={keys} onSubmit={(body) => run(() => addRule(set.id, body))} resetOnSuccess />
        </section>
      )}
    </div>
  );
}

/* ── the rule form ─────────────────────────────────────────────────────── */

function SelectorPicker({ label, value, onChange, keys }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const known = keys ?? [];
  const extra = value.keys.filter((k) => !known.some((x) => x.key === k)).map((k) => ({ key: k, name: k }));
  return (
    <fieldset className="flex flex-col gap-1.5 rounded border border-bg-border px-2.5 py-2" aria-label={label}>
      <legend className="op-label px-1 uppercase">{label}</legend>
      <div className="flex flex-wrap gap-2">
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Category</span>
          <select className={field} value={value.category} onChange={(e) => set({ category: e.target.value })} aria-label={`${label} category`}>
            <option value="">Any</option>
            {SELECTOR_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>)}
          </select></label>
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Well</span>
          <select className={field} value={value.well} onChange={(e) => set({ well: e.target.value })} aria-label={`${label} well link`}>
            <option value="">Any</option>
            <option value="true">Linked to a well</option>
            <option value="false">Not linked to a well</option>
          </select></label>
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Layer</span>
          <input className={`${field} w-28 op-mono`} value={value.layer} maxLength={40} placeholder="any" list={`pp-line-types-${label}`} onChange={(e) => set({ layer: e.target.value })} aria-label={`${label} layer`} />
          {/* A line is selected by its type, its layer (156): offered, not imposed. */}
          <datalist id={`pp-line-types-${label}`}>{LINE_TYPES.map((t) => <option key={t} value={t}>{LINE_TYPE_LABELS[t]}</option>)}</datalist></label>
      </div>
      <div className="flex flex-col gap-1">
        <span className="op-label uppercase">Templates {value.keys.length ? `(${value.keys.length})` : "(any)"}</span>
        {keys === null && <p className="op-meta">Loading templates…</p>}
        <div className="flex max-h-28 flex-wrap gap-x-3 gap-y-1 overflow-auto">
          {[...known, ...extra].map((t) => (
            <label key={t.key} className="flex items-center gap-1 text-[12px] text-fg-primary">
              <input type="checkbox" checked={value.keys.includes(t.key)} aria-label={`${label} template ${t.key}`}
                onChange={(e) => set({ keys: e.target.checked ? [...value.keys, t.key] : value.keys.filter((k) => k !== t.key) })} />
              <span className="op-mono">{t.key}</span>
            </label>
          ))}
        </div>
      </div>
    </fieldset>
  );
}

/**
 * Add (no rule) or edit (a rule: only what changed is sent, plus a null for
 * an object or limit the new relation no longer takes, which the server asks
 * to be stated).
 */
export function RuleForm({ rule = null, keys, onSubmit, onCancel = null, resetOnSuccess = false }) {
  const blank = { rule_key: "", relation: "min_distance", limit_ft: "", severity: "block", message: "", subject: fromSelector({}), object: fromSelector({}) };
  const from = rule ? {
    rule_key: rule.rule_key, relation: rule.relation, limit_ft: rule.limit_ft ?? "", severity: rule.severity, message: rule.message,
    subject: fromSelector(rule.subject_selector), object: fromSelector(rule.object_selector),
  } : blank;
  const [v, setV] = useState(from);
  const [problem, setProblem] = useState(null);
  const set = (k) => (e) => setV((cur) => ({ ...cur, [k]: e.target.value }));
  const distance = DISTANCE_RELATIONS.includes(v.relation);
  const hasObject = v.relation !== "inside_perimeter";
  const keyList = useMemo(() => keys, [keys]);

  function body() {
    const all = {
      rule_key: v.rule_key.trim(), relation: v.relation, severity: v.severity, message: v.message,
      subject_selector: toSelector(v.subject),
      object_selector: hasObject ? toSelector(v.object) : null,
      limit_ft: distance ? (v.limit_ft === "" ? undefined : Number(v.limit_ft)) : null,
    };
    if (!rule) {
      const out = { ...all };
      if (!hasObject) delete out.object_selector;
      if (!distance) delete out.limit_ft;
      return out;
    }
    const out = {};
    for (const [k, val] of Object.entries(all)) {
      if (val === undefined) { out[k] = val; continue; }
      if (JSON.stringify(rule[k] ?? null) !== JSON.stringify(val)) out[k] = val;
    }
    return out;
  }

  return (
    <form className="flex flex-col gap-2" aria-label={rule ? `Edit ${rule.rule_key}` : "Add a rule"} onSubmit={async (e) => {
      e.preventDefault();
      const b = body();
      if (rule && Object.keys(b).length === 0) { onCancel?.(); return; }
      const checked = validateRuleFields(b, rule);
      if (checked.refusal) { setProblem(`${checked.refusal.error} (${checked.refusal.code})`); return; }
      setProblem(null);
      const ok = await onSubmit(b);
      if (ok && resetOnSuccess) setV(blank);
    }}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Key</span>
          <input className={`${field} w-40 op-mono`} value={v.rule_key} maxLength={48} onChange={set("rule_key")} aria-label="Rule key" /></label>
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Relation</span>
          <select className={field} value={v.relation} onChange={set("relation")} aria-label="Relation">
            {RULE_RELATIONS.map((r) => <option key={r} value={r}>{RELATION_LABELS[r]}</option>)}
          </select></label>
        {distance && (
          <label className="flex flex-col gap-1"><span className="op-label uppercase">Limit (ft)</span>
            <input type="number" step="0.01" min="0.01" max="2640" className={`${field} w-24`} value={v.limit_ft} onChange={set("limit_ft")} aria-label="Limit (ft)" /></label>
        )}
        <label className="flex flex-col gap-1"><span className="op-label uppercase">Severity</span>
          <select className={field} value={v.severity} onChange={set("severity")} aria-label="Severity">
            {RULE_SEVERITIES.map((s) => <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>)}
          </select></label>
        <label className="flex min-w-[240px] flex-1 flex-col gap-1"><span className="op-label uppercase">Message</span>
          <input className={field} value={v.message} maxLength={200} onChange={set("message")} aria-label="Message"
            placeholder="Shown on a failing check, e.g. Flowback at least 100′ from a wellhead" /></label>
      </div>
      <div className="flex flex-wrap gap-2">
        <SelectorPicker label="Subject" value={v.subject} keys={keyList} onChange={(subject) => setV((c) => ({ ...c, subject }))} />
        {hasObject && <SelectorPicker label="Object" value={v.object} keys={keyList} onChange={(object) => setV((c) => ({ ...c, object }))} />}
      </div>
      {problem && <p className="text-sm text-accent-red" role="alert" data-testid="pp-rule-problem">{problem}</p>}
      <div className="flex gap-2">
        <button type="submit" className="op-btn op-btn--primary op-btn--sm">{rule ? "Save rule" : "Add rule"}</button>
        {onCancel && <button type="button" className="op-btn op-btn--sm" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
